import { useState } from "react";
import { AlertTriangle, ChevronDown, Info, StickyNote } from "lucide-react";
import {
  type AlertBlock,
  type Article,
  type ArticleBlock,
  type ArticleLang,
  pickLangField,
  sanitizeRichHtml,
} from "@/lib/article-types";
import { cn } from "@/lib/utils";
import { SocialFollowBox } from "./SocialFollowBox";

/**
 * Renders an Article (list of blocks) inside a movie/series page.
 * Each block may carry per-language fields (e.g. html_en, text_fr); the
 * renderer falls back gracefully to the Arabic default when missing.
 *
 * Desktop (lg+): the article takes the start side (right in RTL, left in LTR)
 * and a sticky right-column is shown on the opposite side. The caller can
 * provide a custom `rightColumn` (e.g. social + comments) or leave the default
 * social-follow box. The sticky column naturally stops at the end of the
 * article — no JS measurement needed for the short-article case.
 */
export function ArticleRenderer({
  article,
  className,
  lang = "ar",
  rightColumn,
}: {
  article?: Article;
  className?: string;
  lang?: ArticleLang;
  rightColumn?: React.ReactNode;
}) {
  if (!article || article.length === 0) return null;
  return (
    <section id="article" style={{ scrollMarginTop: "96px" }} className={cn("container mx-auto px-4 py-8", className)}>
      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-8 lg:items-start">
        <article
          dir="auto"
          className="mx-auto max-w-3xl space-y-5 text-[15px] leading-[1.9] text-foreground/90 lg:mx-0 lg:max-w-none"
        >
          {article.map((b) => <Block key={b.id} block={b} lang={lang} />)}
        </article>
        <div className="hidden lg:block">
          {rightColumn ?? (
            <div className="sticky top-24">
              <SocialFollowBox lang={lang} />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}



function pickCollapseTitle(b: ArticleBlock, lang: ArticleLang): string | undefined {
  return pickLangField<string>(b as unknown as Record<string, unknown>, "collapseTitle", lang);
}

function Collapsible({
  open: openInit,
  title,
  children,
}: { open?: boolean; title?: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(!!openInit);
  return (
    <div className="rounded-xl border border-border bg-card/40 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 px-4 py-2.5 text-sm font-bold hover:bg-accent/50 transition"
      >
        <span>{title || "عرض المزيد"}</span>
        <ChevronDown className={cn("h-4 w-4 transition-transform", open && "rotate-180")} />
      </button>
      {open && <div className="px-4 py-3 border-t border-border">{children}</div>}
    </div>
  );
}

function Block({ block, lang }: { block: ArticleBlock; lang: ArticleLang }) {
  switch (block.type) {
    case "paragraph": {
      const html = pickLangField<string>(block as unknown as Record<string, unknown>, "html", lang) ?? "";
      if (!html.trim()) return null;
      const inner = (
        <p
          className={cn(
            "leading-[1.95]",
            // For RTL/Arabic text we use a soft first-line indent instead of a
            // big Latin-style drop-cap because Arabic letters are connected.
            block.dropCap && "article-dropcap",
          )}
          dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(html) }}
        />
      );
      return block.collapsible
        ? <Collapsible title={pickCollapseTitle(block, lang)}>{inner}</Collapsible>
        : inner;
    }
    case "heading": {
      const text = pickLangField<string>(block as unknown as Record<string, unknown>, "text", lang) ?? "";
      if (!text.trim()) return null;
      const Tag = (block.level === 3 ? "h3" : "h2") as "h2" | "h3";
      return (
        <Tag className={cn(
          "font-extrabold text-foreground mt-2",
          block.level === 2 ? "text-2xl" : "text-xl",
        )}>
          {text}
        </Tag>
      );
    }
    case "list": {
      const items = (pickLangField<string[]>(block as unknown as Record<string, unknown>, "items", lang) ?? [])
        .filter((s) => s && s.trim());
      if (items.length === 0) return null;
      const ListTag = (block.ordered ? "ol" : "ul") as "ol" | "ul";
      const inner = (
        <ListTag className={cn(
          "ps-6 space-y-1.5 marker:text-primary",
          block.ordered ? "list-decimal" : "list-disc",
        )}>
          {items.map((it, i) => (
            // Render as HTML so admins can highlight a word with <b>…</b>.
            <li key={i} dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(it) }} />
          ))}
        </ListTag>
      );
      return block.collapsible
        ? <Collapsible title={pickCollapseTitle(block, lang)}>{inner}</Collapsible>
        : inner;
    }
    case "alert": {
      const inner = <AlertBox block={block} lang={lang} />;
      const title = pickLangField<string>(block as unknown as Record<string, unknown>, "title", lang);
      return block.collapsible
        ? <Collapsible title={pickCollapseTitle(block, lang) || title}>{inner}</Collapsible>
        : inner;
    }
    case "quote": {
      const html = pickLangField<string>(block as unknown as Record<string, unknown>, "html", lang) ?? "";
      if (!html.trim()) return null;
      const author = pickLangField<string>(block as unknown as Record<string, unknown>, "author", lang);
      return (
        <figure className="border-s-4 border-primary ps-4 my-2">
          <blockquote
            className="text-base italic text-foreground/85"
            dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(html) }}
          />
          {author && (
            <figcaption className="mt-1 text-xs text-muted-foreground">— {author}</figcaption>
          )}
        </figure>
      );
    }
    case "image": {
      if (!block.src) return null;
      const alt = pickLangField<string>(block as unknown as Record<string, unknown>, "alt", lang) ?? "";
      const caption = pickLangField<string>(block as unknown as Record<string, unknown>, "caption", lang);
      return (
        <figure className="my-2">
          <img
            src={block.src}
            alt={alt}
            loading="lazy"
            decoding="async"
            className="w-full rounded-xl border border-border object-cover"
          />
          {caption && (
            <figcaption className="mt-1.5 text-center text-xs text-muted-foreground">{caption}</figcaption>
          )}
        </figure>
      );
    }
    case "table": {
      const headers = pickLangField<string[]>(block as unknown as Record<string, unknown>, "headers", lang) ?? block.headers;
      const rows = pickLangField<string[][]>(block as unknown as Record<string, unknown>, "rows", lang) ?? block.rows;
      const inner = (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                {headers.map((h, i) => (
                  <th key={i} className="px-3 py-2 text-start font-bold border-b border-border">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="odd:bg-background even:bg-muted/20">
                  {r.map((c, j) => (
                    <td key={j} className="px-3 py-2 border-b border-border/60">{c}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      return block.collapsible
        ? <Collapsible title={pickCollapseTitle(block, lang)}>{inner}</Collapsible>
        : inner;
    }
  }
}

function AlertBox({ block, lang }: { block: AlertBlock; lang: ArticleLang }) {
  const styles = {
    info: "border-sky-500/40 bg-sky-500/10 text-sky-100",
    warning: "border-amber-500/40 bg-amber-500/10 text-amber-100",
    note: "border-violet-500/40 bg-violet-500/10 text-violet-100",
  }[block.variant];
  const Icon = block.variant === "warning" ? AlertTriangle
    : block.variant === "note" ? StickyNote
    : Info;
  const html = pickLangField<string>(block as unknown as Record<string, unknown>, "html", lang) ?? "";
  const title = pickLangField<string>(block as unknown as Record<string, unknown>, "title", lang);
  return (
    <div className={cn("flex gap-3 rounded-xl border p-3.5", styles)}>
      <Icon className="h-5 w-5 shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1">
        {title && <div className="font-bold mb-1 text-foreground">{title}</div>}
        <div
          className="text-sm leading-relaxed text-foreground/85"
          dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(html) }}
        />
      </div>
    </div>
  );
}
